import {
  Cushion,
  FlexContainer,
  Grid,
  MaxWidth,
  Rectangle,
  Show,
  Typography,
} from '@pitchero/react-ui';
import { DateTime, Interval } from 'luxon';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import RosBottomBanner from '../adverts/ros-bottom-banner';
import RosBottomMpu from '../adverts/ros-bottom-mpu';
import TopBannerVideoPlayerSwitch from '../adverts/top-banner-video-player-switch';
import LoadingBar from '../loading-bar';
import SeoHead from '../seo';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import ClubRouter from '../../lib/routing';
import MatchFilterBar from './filter-bar';
import flattenDayEntries from './flatten-entries';
import MatchDay from './match-day';
import LoadingState from './loading-state';
import CalendarComponent from './calendar';
import NextAndPreviousControls from './next-previous';

const Matches = ({ calendar, club, eventType, isLoading, month, teamId, year, t }) => {
  const [validDates, setValidDates] = useState([]);
  const now = DateTime.now();
  const [desiredDay, setDesiredDay] = useState(
    now.month === month && now.year === year ? now.day : 1,
  );
  const [desiredMonth, setDesiredMonth] = useState(month);
  const currentMonth = DateTime.fromObject({ year, month });
  const [desiredYear, setDesiredYear] = useState(year);

  const onChangeTeamHandler = (selectedTeamId) => {
    ClubRouter.replaceClubRoute(club, 'matches.month', {
      year,
      month,
      teamId: selectedTeamId,
      eventType,
    });
  };

  const onChangeEventTypeHandler = (selectedEventType) => {
    ClubRouter.replaceClubRoute(club, 'matches.month', {
      year,
      month,
      teamId,
      eventType: selectedEventType,
    });
  };

  const onChangeMonthHandler = (isPrevious) => {
    const newMonth = isPrevious
      ? currentMonth.minus({ months: 1 })
      : currentMonth.plus({ months: 1 });
    ClubRouter.replaceClubRoute(club, 'matches.month', {
      year: newMonth.year,
      month: newMonth.month,
      teamId,
      eventType,
    });
    setDesiredDay(1);
    setDesiredMonth(newMonth.month);
  };

  const onChangeDayHandler = (date) => {
    const dateTime = DateTime.fromFormat(date, 'yyyy-MM-dd');
    setDesiredDay(dateTime.day);
    setDesiredMonth(dateTime.month);
  };

  useEffect(() => {
    const desired = DateTime.fromObject({
      year: desiredYear,
      month: desiredMonth,
      day: desiredDay,
    });
    const lastMonday = desired.startOf('week');
    const nextMonday = lastMonday.plus({ weeks: 1 });
    const range = Interval.fromDateTimes(lastMonday, nextMonday); // Mon-Sun as fromDateTimes is exclusive of end
    const dates = range.splitBy({ days: 1 }).map((day) => day.start.toFormat('yyyy-MM-dd'));
    setValidDates(dates);
  }, [desiredDay, desiredMonth, desiredYear]);

  const renderContent = () => {
    if (isLoading || !calendar) return null;
    const daysInRange = calendar.days.filter((day) => validDates.includes(day.date));
    const eventCount = daysInRange.reduce(
      (count, day) => count + day.fixtures.length + day.trainingSessions.length,
      0,
    );

    if (eventCount === 0) {
      return (
        <FlexContainer style={{ width: '100%' }} justifyContent="center">
          <Typography preset="description" size={18}>
            No events to show
          </Typography>
        </FlexContainer>
      );
    }

    return (
      <>
        {daysInRange.map((day) => {
          const sortedData = flattenDayEntries(day);
          return (
            <Cushion key={day.day} vertical="large">
              <MatchDay club={club} date={day.date} sortedData={sortedData} />
            </Cushion>
          );
        })}
        <NextAndPreviousControls
          month={month}
          year={year}
          eventType={eventType}
          teamId={teamId}
          desiredDay={desiredDay}
          setDesiredDay={setDesiredDay}
          setDesiredMonth={setDesiredMonth}
          setDesiredYear={setDesiredYear}
          validDates={validDates}
          club={club}
          placement="bottom"
        />
      </>
    );
  };

  const { advertisingTier, sportId } = club;

  return (
    <>
      <SeoHead
        club={club}
        descriptionTranslationKey="common:matches_description"
        titleTranslationKey="common:matches_title"
        translationValues={{
          month: DateTime.fromObject({ year, month, day: 1 }).toFormat('LLLL yyyy'),
        }}
        route="matches.month"
        routeParams={{ month, year }}
      />
      <MatchFilterBar
        club={club}
        eventType={eventType}
        month={month}
        teamId={teamId}
        year={year}
        onChangeTeam={onChangeTeamHandler}
        onChangeEventType={onChangeEventTypeHandler}
        t={t}
      />
      <Rectangle fill="#fff" component="div">
        <LoadingBar isLoading={isLoading} />
        <Cushion top="medium">
          <TopBannerVideoPlayerSwitch
            advertisingTier={advertisingTier}
            pageType="calendar.index"
            sportId={sportId}
          />
        </Cushion>
        <Cushion responsive={[{ maxWidth: 'tab', props: { bottom: 'medium' } }]} component="div">
          <MaxWidth component="div" maxWidth={1366} center>
            {isLoading ? (
              <LoadingState />
            ) : (
              <Grid
                columns="1fr"
                columnGap="medium"
                responsive={[
                  {
                    minWidth: 'fullHeader',
                    props: { columns: 'minmax(auto, 970px) minmax(auto, 400px)' },
                  },
                ]}
              >
                <Cushion
                  vertical="medium"
                  responsive={[{ minWidth: 'fullHeader', props: { left: 'medium' } }]}
                >
                  <NextAndPreviousControls
                    month={month}
                    year={year}
                    eventType={eventType}
                    teamId={teamId}
                    desiredDay={desiredDay}
                    setDesiredDay={setDesiredDay}
                    setDesiredMonth={setDesiredMonth}
                    setDesiredYear={setDesiredYear}
                    validDates={validDates}
                    club={club}
                    placement="top"
                  />
                  {renderContent()}
                </Cushion>
                <Show minWidth="fullHeader" component="div">
                  <Cushion vertical="medium" component="div" right="medium">
                    <CalendarComponent
                      month={month}
                      year={year}
                      data={calendar?.days}
                      validDates={validDates}
                      onMonthClick={onChangeMonthHandler}
                      onDayClick={onChangeDayHandler}
                      isMatchesSection
                      isMobile
                    />
                    <Cushion top="medium">
                      <RosBottomMpu />
                    </Cushion>
                  </Cushion>
                </Show>
              </Grid>
            )}
          </MaxWidth>
        </Cushion>
        <Cushion vertical="large">
          <RosBottomBanner />
        </Cushion>
      </Rectangle>
    </>
  );
};

Matches.defaultProps = {
  calendar: null,
  eventType: null,
  teamId: null,
};

Matches.propTypes = {
  club: CLUB_PROP_TYPES.isRequired,
  calendar: PropTypes.shape(),
  eventType: PropTypes.oneOf(['events', 'fixtures', 'training']),
  isLoading: PropTypes.bool.isRequired,
  month: PropTypes.number.isRequired,
  teamId: PropTypes.number,
  year: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
};

export default Matches;
